/*
 * Pestaña Resumen: KPIs, valor por año, pendiente por gerencia, por tipo,
 * top clientes importación y antigüedad de cartera (sin importación).
 * Réplica del tab-resumen del HTML original.
 */
import { useMemo } from "react";
import { Description } from "codekit";

import Barlistx from "./Barlistx";
import Kpicards from "./Kpicards";
import Panelbox from "./Panelbox";
import Semcards from "./Semcards";
import Simtable, { Dotspanx, Valspanx } from "./Simtable";
import Stakbars from "./Stakbars";
import Vertbars from "./Vertbars";
import Yearcard from "./Yearcard";
import { bucketAgingGeneral, bucketColors, carteraColors, fmtCOP, groupSum, htmlTheme, truncate } from "./utils/carthelp";

const IMP = "IMPORTACION";
const AGING_KEYS = ["GERENCIA", "NOMBRE", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"];

/**
 * Ordena un mapa de sumas en filas de barra con color
 * @param {Object} mMap Mapa clave → valor
 * @param {Function} colorAt Función índice → color
 * @param {number} nTop Máximo de filas
 * @returns {Array} Filas [{ label, value, color }]
 */
const toBars = (mMap, colorAt, nTop, labelFn) =>
  Object.entries(mMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, nTop)
    .map(([sLabel, nValue], nIndex) => ({
      label: labelFn ? labelFn(sLabel, nValue) : sLabel,
      raw: sLabel,
      value: nValue,
      color: colorAt(nIndex),
    }));

/**
 * Arma el texto y color de la variación contra un snapshot guardado
 * @param {number} nTotal Pendiente de la carga actual
 * @param {Object} mCompare Snapshot de referencia { label, total }
 * @returns {Object} { delta, deltaColor }
 */
const buildDelta = (nTotal, mCompare) => {
  if (!mCompare) return { delta: "", deltaColor: "" };
  const nDiff = nTotal - mCompare.total;
  const nPct = mCompare.total !== 0 ? (nDiff / Math.abs(mCompare.total)) * 100 : 0;
  const sArrow = nDiff > 0 ? "▲" : nDiff < 0 ? "▼" : "—";
  const sColor = nDiff > 0 ? htmlTheme.coral : nDiff < 0 ? htmlTheme.green : htmlTheme.muted;
  return {
    delta: `${sArrow} ${fmtCOP(Math.abs(nDiff))} (${nPct.toFixed(1)}%) vs ${mCompare.label}`,
    deltaColor: sColor,
  };
};

/**
 * Construye los KPIs de la vista
 * @param {Array} mData Datos de trabajo
 * @param {Object} mCompare Snapshot de referencia { label, total }
 * @returns {Array} KPIs [{ label, value, delta, deltaColor }]
 */
const buildKpis = (mData, mCompare) => {
  const nTotal = mData.reduce((a, r) => a + r.VALOR, 0);
  const mByCliente = groupSum(mData, "NOMBRE");
  const mTop = Object.entries(mByCliente).sort((a, b) => b[1] - a[1])[0];
  return [
    { label: "Valor Total Pendiente", value: fmtCOP(nTotal), ...buildDelta(nTotal, mCompare) },
    { label: "DO Pendientes", value: mData.length.toLocaleString("es-CO"), delta: "" },
    {
      label: "Cliente Mayor Exposición",
      value: mTop ? mTop[0].substring(0, 26) : "-",
      delta: mTop ? fmtCOP(mTop[1]) : "",
    },
  ];
};

/**
 * Agrupa las filas de antigüedad en rangos y por cliente
 * @param {Array} mRows Filas sin importación con antigüedad válida
 * @returns {Object} { groups, byClient, critical }
 */
const buildAging = (mRows) => {
  const mGroups = {
    verde: { count: 0, valor: 0, rows: [] },
    amarillo: { count: 0, valor: 0, rows: [] },
    rojo: { count: 0, valor: 0, rows: [] },
  };
  const mByClient = {};
  mRows.forEach((r) => {
    const sB = bucketAgingGeneral(r._dias);
    mGroups[sB].count++;
    mGroups[sB].valor += r.VALOR;
    mGroups[sB].rows.push(r);
    if (!mByClient[r.NOMBRE]) mByClient[r.NOMBRE] = { verde: 0, amarillo: 0, rojo: 0, total: 0 };
    mByClient[r.NOMBRE][sB] += r.VALOR;
    mByClient[r.NOMBRE].total += r.VALOR;
  });
  const mByClientTop = Object.entries(mByClient)
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 15)
    .map(([sLabel, v]) => ({ label: truncate(sLabel, 28), raw: sLabel, ...v }));
  const mCritical = mRows
    .filter((r) => bucketAgingGeneral(r._dias) === "rojo")
    .sort((a, b) => b._dias - a._dias)
    .slice(0, 30);
  return { groups: mGroups, byClient: mByClientTop, critical: mCritical };
};

const criticalColumns = [
  { key: "DO", title: "DO" },
  { key: "NOMBRE", title: "Cliente" },
  { key: "GERENCIA", title: "Gerencia" },
  { key: "TIPO DE OPERACIÓN", title: "Tipo" },
  {
    key: "_dias",
    title: "Días",
    render: (r) => (
      <span>
        <Dotspanx color={bucketColors.rojo} />
        {r._dias}
      </span>
    ),
  },
  { key: "VALOR", title: "Valor", render: (r) => <Valspanx value={r.VALOR} text={fmtCOP(r.VALOR)} /> },
];

//Hint de antigüedad con los puntos de color del HTML
const agingHint = (
  <span>
    Importación se analiza aparte en su pestaña · corte: <Dotspanx color={bucketColors.verde} />
    0-30 días · <Dotspanx color={bucketColors.amarillo} />
    31-60 días · <Dotspanx color={bucketColors.rojo} />
    +60 días desde creación
  </span>
);

/**
 * Renderiza la pestaña Resumen
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Datos de trabajo filtrados
 * @param {Function} props.onFunnel Abre el drill-down (scope, keys, breadcrumb)
 * @param {Object} props.compare Snapshot de referencia { label, total }
 */
const Tabresum = ({ data, onFunnel, compare }) => {
  const mKpis = useMemo(() => buildKpis(data, compare), [data, compare]);
  const mYears = useMemo(() => {
    const mBy = {};
    data.forEach((r) => {
      const sY = r.ANIO || "S/A";
      if (!mBy[sY]) mBy[sY] = { valor: 0, count: 0 };
      mBy[sY].valor += r.VALOR;
      mBy[sY].count++;
    });
    return Object.keys(mBy)
      .sort((a, b) => (a === "S/A" ? 9999 : +a) - (b === "S/A" ? 9999 : +b))
      .map((sY) => ({ year: sY, valor: mBy[sY].valor, count: mBy[sY].count }));
  }, [data]);
  const mGerencia = useMemo(() => toBars(groupSum(data, "GERENCIA"), (i) => carteraColors[i % carteraColors.length], 99), [data]);
  const mTipo = useMemo(
    () => toBars(groupSum(data, "TIPO DE OPERACIÓN"), (i) => carteraColors[i % carteraColors.length], 99, (k, v) => `${k}  —  ${fmtCOP(v)}`),
    [data],
  );
  const mCliImp = useMemo(
    () => toBars(groupSum(data.filter((r) => r["TIPO DE OPERACIÓN"] === IMP), "NOMBRE"), () => htmlTheme.coral, 15, (k) => truncate(k, 32)),
    [data],
  );
  const mAging = useMemo(
    () => buildAging(data.filter((r) => r["TIPO DE OPERACIÓN"] !== IMP && r._dias !== null)),
    [data],
  );

  //Abre el año seleccionado en el drill-down
  const openYear = (sYear) => {
    const mScope = data.filter((r) => String(r.ANIO ?? "S/A") === String(sYear));
    onFunnel(mScope, AGING_KEYS, [`Año ${sYear}`]);
  };

  return (
    <>
      <Kpicards kpis={mKpis} />
      <Panelbox title="Valor Pendiente por Año del DO" hint="Click en un año → Gerencia → Cliente → Tipo de Operación → Director de Cuenta → detalle de DOs">
        <Yearcard years={mYears} onYearClick={openYear} />
      </Panelbox>
      <Panelbox title="Pendiente por Gerencia" hint="Click en una barra → Cliente → Tipo de Operación → Director de Cuenta → detalle de DOs (todo en una ventana)">
        <Vertbars
          data={mGerencia}
          height={260}
          onItemClick={(m) => onFunnel(data.filter((r) => r.GERENCIA === m.raw), ["NOMBRE", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"], [`Gerencia ${m.raw}`])}
        />
      </Panelbox>
      <Panelbox title="Por Tipo de Operación" hint="Ordenado de mayor a menor — el valor va incluido en cada barra">
        <Barlistx
          data={mTipo}
          height={300}
          labelWidth={250}
          onItemClick={(m) => onFunnel(data.filter((r) => r["TIPO DE OPERACIÓN"] === m.raw), ["DIRECTOR DE CUENTA"], [`Tipo ${m.raw}`])}
        />
      </Panelbox>
      <Panelbox title="Top Clientes con Mayor Exposición en Importación" hint="De mayor a menor — identifica de una mirada quién concentra el riesgo de importaciones">
        <Barlistx
          data={mCliImp}
          height={400}
          onItemClick={(m) => onFunnel(data.filter((r) => r["TIPO DE OPERACIÓN"] === IMP && r.NOMBRE === m.raw), ["TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"], [`Cliente ${m.raw}`])}
        />
      </Panelbox>
      <Panelbox title="Antigüedad de Cartera (sin Importación)" hint={agingHint}>
        <Semcards
          groups={mAging.groups}
          onCardClick={(sK) => onFunnel(mAging.groups[sK].rows, AGING_KEYS, [`Antigüedad · ${sK}`])}
        />
        <Stakbars
          data={mAging.byClient}
          legend={["0-30 días", "31-60 días", "+60 días"]}
          height={400}
          onItemClick={(m) =>
            onFunnel(
              data.filter((r) => r["TIPO DE OPERACIÓN"] !== IMP && r._dias !== null && r.NOMBRE === m.raw),
              ["GERENCIA", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"],
              [`Antigüedad · Cliente ${m.raw}`],
            )
          }
        />
        <div style={{ color: htmlTheme.muted, margin: "14px 0 8px" }}>
          <Description variant="description-3">
            DOs más críticos (mayor antigüedad) — click en la gráfica de arriba para ver detalle por cliente
          </Description>
        </div>
        <Simtable
          columns={criticalColumns}
          rows={mAging.critical}
          onRowClick={(r) => onFunnel(data.filter((x) => x.NOMBRE === r.NOMBRE && x["TIPO DE OPERACIÓN"] !== IMP), ["GERENCIA", "TIPO DE OPERACIÓN", "DIRECTOR DE CUENTA"], [`Cliente ${r.NOMBRE}`])}
        />
      </Panelbox>
    </>
  );
};

export default Tabresum;
